import { Injectable } from '@angular/core';
import { Router, UrlSegment } from '@angular/router';

import { BehaviorSubject, finalize, forkJoin, Observable } from 'rxjs';

import { ERoutes } from 'src/app/core/enums/routes.enum';
import { ESvgIcons } from 'src/app/core/enums/svg-icons';
import { EInfoType } from 'src/app/core/enums/country-info-type.enum';
import { CityResponse } from 'src/app/core/types/models/city-response.model';
import { RegionResponse } from 'src/app/core/types/models/region-response.model';
import { CountryResponse } from 'src/app/core/types/models/country-response.model';
import { OccasionGeniusResponse } from 'src/app/core/types/models/occasion-genius-response.model';

import { ICityResponse } from 'src/app/core/types/interfaces/city-response.interface';
import { IRegionResponse } from 'src/app/core/types/interfaces/region-response.interface';
import { IViatorCategories } from 'src/app/shared/used-components/viator/viator.component';
import { ICountryResponse } from 'src/app/core/types/interfaces/country-response.interface';
import { IOccasionGeniusResponse } from 'src/app/core/types/interfaces/occasion-genius-response.interface';

import { PackageService } from 'src/app/core/services/package.service';
import { CountryInfoService } from 'src/app/core/services/country-info.service';
import { VoiceMapResponse } from 'src/app/core/types/models/voice-map-response';
import { OccasionGeniusService } from 'src/app/core/services/occasion-genius.service';
import { MetaTagsConfigService } from 'src/app/core/services/meta-tags-config.service';
import { IVoiceMapResponse } from 'src/app/core/types/interfaces/voice-map-response.interface';

import moment from 'moment';

@Injectable()
export class CountryInfoParseService {
  public readonly eSvgIcons = ESvgIcons;
  public readonly ERoutes = ERoutes;
  public readonly eInfoType = EInfoType;

  public breadcrumbs: { text: string; path: string }[] = [];
  public menuOpened: boolean[] = [false, false, false];

  public readonly countryResponse$ = new BehaviorSubject<CountryResponse>(
    new CountryResponse(),
  );

  public readonly regionResponse$ = new BehaviorSubject<RegionResponse>(
    new RegionResponse(),
  );

  public readonly cityResponse$ = new BehaviorSubject<CityResponse>(
    new CityResponse(),
  );

  public readonly hotelsResponse$ = new BehaviorSubject<any[]>([]);

  public readonly categoriesResponse$ = new BehaviorSubject<IViatorCategories>(
    {},
  );

  public readonly occasionGeniusResponse$ =
    new BehaviorSubject<OccasionGeniusResponse>(new OccasionGeniusResponse());

  public readonly voiceMapResponse$ = new BehaviorSubject<VoiceMapResponse>(
    new VoiceMapResponse(),
  );

  public readonly isFetching$ = new BehaviorSubject<boolean>(true);
  public readonly mapCoordinates$ = new BehaviorSubject<string>('');

  constructor(
    private router: Router,
    private packageService: PackageService,
    private countryInfoService: CountryInfoService,
    private metaTagsConfigService: MetaTagsConfigService,
    private occasionGeniusService: OccasionGeniusService,
  ) {}

  public fetchData(segments: UrlSegment[]): void {
    const [country, region, city] = segments.map((s) => s.path);

    const createObserver = (
      route: string | undefined,
    ): Observable<any> | null => {
      return route ? this.countryInfoService.getCountryInfo(route) : null;
    };

    if (country && country.includes('things-to-do-in-')) return;

    const countryObserver = createObserver(country ? `/${country}` : undefined);
    this.setFetching();

    if (countryObserver) {
      countryObserver.subscribe({
        next: (response: ICountryResponse | string) => {
          if (response && typeof response !== 'string') {
            this.initCountry(response);
          } else {
            this.router.navigate([ERoutes.SLASH, ERoutes.ERROR]).then();
          }
        },
        complete: () => {
          const regionObserver = createObserver(
            region ? `/${country}/${region}` : undefined,
          );
          const cityObserver = createObserver(
            city ? `/${country}/${region}/${city}` : undefined,
          );

          if (regionObserver) {
            regionObserver.subscribe({
              next: (response: IRegionResponse | string) => {
                if (response && typeof response !== 'string') {
                  this.initRegion(response);
                } else {
                  this.router.navigate([ERoutes.SLASH, ERoutes.ERROR]).then();
                }
              },
              error: (err) => {
                console.error('Error fetching region data:', err);
              },
            });
          }

          if (cityObserver) {
            cityObserver
              .pipe(
                finalize(() => {
                  this.isFetching$.next(false);
                }),
              )
              .subscribe({
                next: (response: ICityResponse) => {
                  if (response && typeof response !== 'string') {
                    this.initCity(response);
                  } else {
                    this.isFetching$.next(false);
                    this.router.navigate([ERoutes.SLASH, ERoutes.ERROR]).then();
                  }
                },
                error: (err) => {
                  console.error('Error fetching city data:', err);
                },
              });
          } else {
            setTimeout(() => this.isFetching$.next(false), 1_000);
          }
        },
        error: (err) => {
          console.error('Error fetching country data:', err);
        },
      });
    }
  }

  public setFetching(): void {
    this.isFetching$.next(true);
    this.countryResponse$.next(new CountryResponse());
    this.regionResponse$.next(new RegionResponse());
    this.cityResponse$.next(new CityResponse());
    this.hotelsResponse$.next([]);
    this.categoriesResponse$.next({});
    this.occasionGeniusResponse$.next(new OccasionGeniusResponse());
    this.voiceMapResponse$.next(new VoiceMapResponse());
  }

  public initOccasionGeniusResponse(response: IOccasionGeniusResponse): void {
    const occasionGeniusResponse = new OccasionGeniusResponse(response);
    this.occasionGeniusResponse$.next(occasionGeniusResponse);
  }

  private initRegion(response: IRegionResponse): void {
    const regionResponse = new RegionResponse(response);
    this.regionResponse$.next(regionResponse);
  }

  private initCity(response: ICityResponse): void {
    const cityResponse = new CityResponse(response);
    this.cityResponse$.next(cityResponse);

    forkJoin({
      categories: this.packageService.getViatorEventsByCityId(
        cityResponse.cityInfo.getFirstCity().place_id,
      ),
      voiceMapResponse: this.countryInfoService.getVoiceMapInfoList(
        cityResponse.cityInfo.getFirstCity().place_id,
      ),
      occasionGeniusResponse: this.occasionGeniusService.getOccasionGeniusAll(
        cityResponse.cityInfo.getFirstCity().place_id,
        {
          from: moment().add(1, 'days').unix(),
          to: moment().add(1, 'days').add(1, 'year').unix(),
          offset: 0,
        },
      ),
    })
      .pipe(finalize(() => this.isFetching$.next(false)))
      .subscribe(({ categories, voiceMapResponse, occasionGeniusResponse }) => {
        if (categories) {
          this.categoriesResponse$.next(categories);
        }

        if (voiceMapResponse) {
          this.initVoiceMapResponse(voiceMapResponse);
        }

        if (occasionGeniusResponse) {
          this.initOccasionGeniusResponse(occasionGeniusResponse);
        }
      });
  }

  private initVoiceMapResponse(response: IVoiceMapResponse): void {
    const voiceMapResponse = new VoiceMapResponse(response);
    this.voiceMapResponse$.next(voiceMapResponse);
  }

  private initCountry(response: ICountryResponse): void {
    const countryResponse = new CountryResponse(response);
    const countryDetails = countryResponse.countryInfo.getFirstCountry();
    this.countryResponse$.next(countryResponse);
    this.mapCoordinates$.next(countryDetails.coordinates);
    this.metaTagsConfigService.setMetaTagsDynamically(
      ERoutes.COUNTRIES,
      `RoutePerfect Free Trip Planner To ${countryDetails.name} - The Smart Way To Plan Your Dream Trip`,
      `Plan your Trip to ${countryDetails.name} - Free ${countryDetails.name} trip planner`,
    );
  }
}
